
import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import type { Session } from '@supabase/supabase-js';

interface AuthUser {
  id: string;
  email?: string;
  user_metadata?: {
    [key: string]: any;
  }
}

interface UserProfile {
  id: string;
  full_name: string;
  nickname: string;
  age?: number;
  gender?: string;
  country?: string;
  points: number;
  is_verified: boolean;
  created_at: string;
}

export const useAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { language } = useLanguage();

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      setUserProfile(data);
    } catch (error: any) {
      console.error('Error fetching user profile:', error.message);
    }
  };

  // 檢查個人資料是否完整
  const checkProfileCompleteness = async (userId: string) => {
    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('full_name, nickname, country, is_verified')
        .eq('id', userId)
        .single();

      console.log('useAuth - Profile completeness check:', profileData, error);

      // 如果沒有個人資料或資料不完整，導向完成註冊頁面
      if (error || !profileData?.full_name || !profileData?.nickname || !profileData?.country) {
        console.log('useAuth - Profile incomplete, navigating to signup complete');
        navigate('/signup/complete');
        return false;
      }

      return true;
    } catch (error) {
      console.error('useAuth - Error checking profile:', error);
      navigate('/signup/complete');
      return false;
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          setSession(session);
          await fetchUserProfile(session.user.id);
        }
      } catch (error: any) {
        console.error('Error fetching auth user:', error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();

    // 監聽認證狀態變化
    const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('useAuth - Auth state change:', event, !!session);
      
      if (session?.user) {
        setUser(session.user);
        setSession(session);
        setIsLoading(false);
        
        // 對於 Google 登入，檢查個人資料完整性
        if (event === 'SIGNED_IN' && session.user.app_metadata.provider === 'google') {
          console.log('useAuth - Google signin detected, checking profile');
          setTimeout(async () => {
            await checkProfileCompleteness(session.user.id);
            await fetchUserProfile(session.user.id);
          }, 0);
        } else {
          // 其他情況正常獲取個人資料
          setTimeout(() => {
            fetchUserProfile(session.user.id);
          }, 0);
        }
      } else {
        setUser(null);
        setUserProfile(null);
        setSession(null);
        setIsLoading(false);
      }
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, [navigate]);

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: language === 'en' ? 'Signed out successfully' : '登出成功',
        description: language === 'en' ? 'You have been signed out' : '您已成功登出',
      });
    } catch (error: any) {
      console.error('Error signing out:', error.message);
      toast({
        variant: "destructive",
        title: language === 'en' ? 'Error signing out' : '登出錯誤',
        description: error.message,
      });
    }
  }, [toast, language]);

  // 使用 useMemo 來穩定返回值的引用
  const authValue = useMemo(() => ({
    user,
    userProfile,
    session,
    isLoading,
    isLoggedIn: !!user,
    signOut,
  }), [user, userProfile, session, isLoading, signOut]);

  return authValue;
};
